import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Button,
  Card,
  DatePicker,
  Space,
  Spin,
  Typography,
  message,
} from 'antd';
import {
  ArrowLeftOutlined,
  ReloadOutlined,
  ExperimentOutlined,
} from '@ant-design/icons';
import dayjs, { Dayjs } from 'dayjs';
import { Link } from 'react-router';
import { authFetch } from '../../utils/api';
import {
  ANTI_DETECTION_SIGNAL_CATALOG,
  type AntiDetectionSignalKey,
  type AntiDetectionSignalSpec,
} from '../../shared/anti-detection/signals';
import {
  CorrelationHeatmap,
  CorrelationLegend,
  type CorrelationCell,
} from './CorrelationHeatmap';
import { DualSignalOverlay } from './DualSignalOverlay';

const { Title, Paragraph } = Typography;
const { RangePicker } = DatePicker;

/**
 * Sprint 25 / S25-M — Anti-detection cross-correlation matrix page.
 *
 * Renders the 16×16 Pearson heatmap with a brand-aware filter + range picker.
 * Clicking a cell loads the dual-signal hourly overlay for the chosen pair.
 *
 * Route: /admin/anti-detection-telemetry/correlation
 *
 * Backed by:
 *   - GET /api/admin/anti-detection-correlation
 *   - GET /api/admin/anti-detection-correlation/cell?row=&col=
 */

interface MatrixResponse {
  signals: AntiDetectionSignalSpec[];
  hourBuckets: string[];
  series: Record<AntiDetectionSignalKey, number[]>;
  cells: CorrelationCell[];
  windowStart: string;
  windowEnd: string;
  brand: string | null;
  strongPairCount: number;
  isolatedSignalCount: number;
}

interface DrillDownResponse {
  row: AntiDetectionSignalSpec;
  col: AntiDetectionSignalSpec;
  hourBuckets: string[];
  rowSeries: number[];
  colSeries: number[];
  r: number;
  strong: boolean;
}

export const AntiDetectionCorrelationPage: React.FC = () => {
  const [range, setRange] = useState<[Dayjs, Dayjs]>(() => [
    dayjs().subtract(24, 'hour').startOf('hour'),
    dayjs().startOf('hour'),
  ]);
  const [matrix, setMatrix] = useState<MatrixResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState<number | null>(null);
  const [selected, setSelected] = useState<{
    row: AntiDetectionSignalKey;
    col: AntiDetectionSignalKey;
  } | null>(null);
  const [drill, setDrill] = useState<DrillDownResponse | null>(null);
  const [drillLoading, setDrillLoading] = useState(false);

  const from = range[0]?.toISOString();
  const to = range[1]?.toISOString();

  const fetchMatrix = useCallback(async () => {
    if (!from || !to) return;
    setLoading(true);
    try {
      const params = new URLSearchParams({ from, to });
      const res = await authFetch(
        `/api/admin/anti-detection-correlation?${params.toString()}`
      );
      const result = await res.json();
      if (result.code === 0 && result.data) {
        setMatrix(result.data as MatrixResponse);
      } else {
        message.warning(result.msg || '取得 correlation matrix 失敗');
      }
    } catch {
      message.error('取得 correlation matrix 失敗');
    } finally {
      setLoading(false);
    }
  }, [from, to]);

  const fetchDrill = useCallback(
    async (row: AntiDetectionSignalKey, col: AntiDetectionSignalKey) => {
      if (!from || !to) return;
      setDrillLoading(true);
      try {
        const params = new URLSearchParams({ row, col, from, to });
        const res = await authFetch(
          `/api/admin/anti-detection-correlation/cell?${params.toString()}`
        );
        const result = await res.json();
        if (result.code === 0 && result.data) {
          setDrill(result.data as DrillDownResponse);
        } else {
          message.warning(result.msg || '取得 cell 詳情失敗');
        }
      } catch {
        message.error('取得 cell 詳情失敗');
      } finally {
        setDrillLoading(false);
      }
    },
    [from, to]
  );

  useEffect(() => {
    fetchMatrix();
  }, [fetchMatrix]);

  useEffect(() => {
    if (selected) {
      fetchDrill(selected.row, selected.col);
    } else {
      setDrill(null);
    }
  }, [selected, fetchDrill]);

  const strongPairs = useMemo(() => {
    if (!matrix) return [];
    // Each unordered pair appears twice (row→col and col→row); dedup by
    // sorting the pair.
    const seen = new Set<string>();
    const out: CorrelationCell[] = [];
    for (const c of matrix.cells) {
      if (!c.strong || c.row === c.col) continue;
      const key = [c.row, c.col].sort().join('|');
      if (seen.has(key)) continue;
      seen.add(key);
      out.push(c);
    }
    return out.sort((a, b) => Math.abs(b.r) - Math.abs(a.r));
  }, [matrix]);

  return (
    <div style={{ padding: 16 }}>
      <Space style={{ marginBottom: 8 }}>
        <Link to="/anti-detection-telemetry">
          <Button icon={<ArrowLeftOutlined />}>返回 16-tile 總覽</Button>
        </Link>
      </Space>
      <Title level={3}>
        <ExperimentOutlined /> Anti-detection cross-correlation matrix
      </Title>
      <Paragraph type="secondary">
        16 個 anti-detection signal 在 24 小時 hour-bucket 上的 Pearson
        product-moment 相關係數矩陣。Cell 顏色：紅 |r| ≥ 0.7（強相關，
        正/逆均標示）、橙 0.5 ≤ |r| &lt; 0.7、黃 0.3 ≤ |r| &lt; 0.5、灰
        |r| &lt; 0.3（噪聲）。點擊非對角 cell 取得該 pair 的 24h dual-signal
        時序疊圖。資料源：connectivity_event / fire_marker_event /
        decoy_connection / audit_log。
      </Paragraph>

      <Card style={{ marginBottom: 16 }}>
        <Space wrap>
          <RangePicker
            showTime
            value={range}
            allowClear={false}
            onChange={(vals) => {
              if (vals && vals[0] && vals[1]) {
                setRange([vals[0], vals[1]]);
              }
            }}
          />
          <Button
            type="primary"
            icon={<ReloadOutlined />}
            onClick={fetchMatrix}
            loading={loading}
          >
            重新查詢
          </Button>
        </Space>
      </Card>

      {matrix?.isolatedSignalCount === ANTI_DETECTION_SIGNAL_CATALOG.length ? (
        <Alert
          showIcon
          type="info"
          message="此區間沒有任何 anti-detection signal 觸發，矩陣全為 0。"
          style={{ marginBottom: 12 }}
        />
      ) : null}

      <Card
        title="Heatmap"
        loading={loading}
        extra={
          matrix ? (
            <Space size={12}>
              <span>視窗：</span>
              <span>
                {dayjs(matrix.windowStart).format('YYYY-MM-DD HH:mm')}
                {' → '}
                {dayjs(matrix.windowEnd).format('YYYY-MM-DD HH:mm')}
              </span>
              <span>· 品牌：{matrix.brand ?? '全品牌'}</span>
            </Space>
          ) : null
        }
      >
        {matrix ? (
          <div
            style={{
              display: 'flex',
              flexWrap: 'wrap',
              gap: 32,
              alignItems: 'flex-start',
            }}
          >
            <div style={{ overflowX: 'auto' }}>
              <CorrelationHeatmap
                cells={matrix.cells}
                highlightedIndex={highlightedIndex}
                onHighlightChange={setHighlightedIndex}
                onCellClick={(row, col) => setSelected({ row, col })}
              />
            </div>
            <div style={{ minWidth: 260 }}>
              <CorrelationLegend
                strongPairCount={matrix.strongPairCount}
                isolatedSignalCount={matrix.isolatedSignalCount}
              />
              {strongPairs.length > 0 ? (
                <div style={{ marginTop: 16 }}>
                  <Title level={5} style={{ marginBottom: 8 }}>
                    Top strong pairs ({strongPairs.length})
                  </Title>
                  <Space direction="vertical" size={4}>
                    {strongPairs.slice(0, 6).map((c) => (
                      <Button
                        key={`${c.row}-${c.col}`}
                        type="text"
                        size="small"
                        onClick={() =>
                          setSelected({ row: c.row, col: c.col })
                        }
                      >
                        {labelOf(c.row)} × {labelOf(c.col)} · r={' '}
                        {c.r.toFixed(2)}
                      </Button>
                    ))}
                  </Space>
                </div>
              ) : null}
            </div>
          </div>
        ) : null}
      </Card>

      <Spin spinning={drillLoading}>
        {drill ? (
          <DualSignalOverlay
            row={drill.row}
            col={drill.col}
            hourBuckets={drill.hourBuckets}
            rowSeries={drill.rowSeries}
            colSeries={drill.colSeries}
            r={drill.r}
            strong={drill.strong}
          />
        ) : null}
      </Spin>
    </div>
  );
};

function labelOf(key: AntiDetectionSignalKey): string {
  return (
    ANTI_DETECTION_SIGNAL_CATALOG.find((s) => s.key === key)?.label ?? key
  );
}

export default AntiDetectionCorrelationPage;
